import React, { useState, useEffect } from 'react';
import { SITE_CONFIG } from './constants';
import { ArrowRight, Mail, ChevronRight, ExternalLink } from 'lucide-react';
import { motion, useScroll, useTransform } from 'motion/react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import Scene3D from './components/Scene3D';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const App: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-pink-500/30 selection:text-pink-200 font-sans overflow-x-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full bg-pink-600/10 blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-cyan-600/10 blur-[120px]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
      </div>

      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={cn(
          "fixed top-0 left-0 w-full z-50 transition-all duration-500 border-b",
          scrolled 
            ? "bg-[#050505]/80 backdrop-blur-xl border-white/10 py-4" 
            : "bg-transparent border-transparent py-6"
        )}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center">
          <div className="flex items-center gap-3 group cursor-pointer">
            <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-cyan-500 text-white rounded-xl flex items-center justify-center font-black text-lg transition-transform duration-500 group-hover:rotate-[15deg] group-hover:scale-110 shadow-[0_0_20px_rgba(236,72,153,0.3)]">
              W
            </div>
            <span className="font-bold text-xl tracking-tight">
              WebX<span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-cyan-500">Hosts</span>
            </span>
          </div>
          <div className="flex items-center gap-8">
            <a href="#services" className="hidden md:block text-sm font-medium text-zinc-400 hover:text-white transition-colors">Services</a>
            <a href="#contact" className="hidden md:block text-sm font-medium text-zinc-400 hover:text-white transition-colors">Contact</a>
            <a href="#contact" className="px-5 py-2.5 bg-white text-black text-sm font-semibold rounded-full hover:bg-pink-50 hover:scale-105 transition-all duration-300 shadow-[0_0_20px_rgba(255,255,255,0.1)] flex items-center gap-2">
              Let's Talk <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </motion.nav>

      <main className="relative z-10">
        {/* Hero Section */}
        <section className="relative min-h-screen flex flex-col items-center justify-center pt-32 pb-20 px-6">
          <Scene3D />
          <motion.div 
            style={{ opacity, scale }}
            className="max-w-5xl mx-auto text-center flex flex-col items-center relative z-10"
          >
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm font-medium text-zinc-300 mb-8 backdrop-blur-md"
            >
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Available for new projects
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="text-6xl md:text-8xl lg:text-9xl font-black tracking-tighter mb-8 leading-[0.9]"
            >
              Crafting <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500">
                Digital Experiences.
              </span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="text-zinc-400 text-lg md:text-2xl max-w-2xl mx-auto mb-12 font-medium leading-relaxed"
            >
              {SITE_CONFIG.hero.subheading}
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col sm:flex-row items-center gap-4"
            >
              <a href="#services" className="px-8 py-4 bg-gradient-to-r from-pink-600 to-purple-600 text-white text-base font-semibold rounded-full hover:scale-105 transition-all duration-300 shadow-[0_0_30px_rgba(236,72,153,0.3)] flex items-center gap-2">
                Explore Services
              </a>
              <a href="#contact" className="px-8 py-4 bg-white/5 text-white text-base font-semibold rounded-full hover:bg-white/10 transition-all duration-300 border border-white/10 flex items-center gap-2 backdrop-blur-sm">
                Get in Touch
              </a>
            </motion.div>
          </motion.div>
        </section>

        {/* Services Section (Bento Grid) */}
        <section id="services" className="py-32 px-6 md:px-12 relative">
          <div className="max-w-7xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              className="mb-20"
            >
              <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">Expertise</h2>
              <p className="text-xl text-zinc-400 max-w-2xl">End-to-end solutions to bring your ideas to life.</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {SITE_CONFIG.services.map((service, idx) => (
                <motion.div 
                  key={idx} 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className={cn(
                    "group relative p-8 rounded-3xl bg-white/[0.02] border border-white/10 hover:bg-white/[0.04] transition-all duration-500 overflow-hidden backdrop-blur-md",
                    idx === 0 ? "md:col-span-2 lg:col-span-2" : "",
                    idx === 3 ? "md:col-span-2 lg:col-span-3" : ""
                  )}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                  
                  <div className="relative z-10 flex flex-col h-full">
                    <div className={cn(
                      "w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-8 transition-all duration-500 group-hover:scale-110",
                      `text-transparent bg-clip-text bg-gradient-to-br ${service.color}`
                    )}>
                      {/* Using a wrapper to apply gradient to icon stroke if possible, or just color it */}
                      <div className={cn("text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-br", service.color)}>
                        {service.icon}
                      </div>
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-3 tracking-tight group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-zinc-400 transition-all">{service.title}</h3>
                    <p className="text-zinc-400 leading-relaxed mb-8 flex-grow">{service.description}</p>
                    
                    <div className="flex items-center text-sm font-semibold text-white/50 group-hover:text-white transition-colors mt-auto">
                      Learn More <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-32 px-6 md:px-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-pink-900/10 border-t border-white/5" />
          <div className="max-w-5xl mx-auto text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-pink-500 to-cyan-500 text-white rounded-full flex items-center justify-center mb-10 shadow-[0_0_40px_rgba(236,72,153,0.3)]">
                <Mail className="w-8 h-8" />
              </div>
              <h2 className="text-5xl md:text-7xl font-black tracking-tight mb-8">Ready to build?</h2>
              <p className="text-zinc-400 text-xl mb-12 max-w-2xl mx-auto">
                Whether you need a full-stack application, rigorous testing, or project management, I'm here to help.
              </p>
              <a 
                href={`mailto:${SITE_CONFIG.contact.email}`} 
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black text-lg font-bold rounded-full hover:bg-pink-50 hover:scale-105 transition-all duration-300 shadow-[0_0_30px_rgba(255,255,255,0.1)]"
              >
                {SITE_CONFIG.contact.email} <ExternalLink className="w-5 h-5" />
              </a>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 border-t border-white/10 bg-[#050505] relative z-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-3">
             <div className="w-8 h-8 bg-gradient-to-br from-pink-500 to-cyan-500 text-white rounded-lg flex items-center justify-center font-black">W</div>
             <span className="text-sm font-semibold text-zinc-500">© {new Date().getFullYear()} {SITE_CONFIG.name}. All rights reserved.</span>
          </div>
          <div className="flex gap-4">
            {SITE_CONFIG.contact.socials.map((social, i) => (
              <a 
                key={i} 
                href={social.link} 
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all duration-300" 
                aria-label={social.name}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;